import { useEffect, useState } from 'react';
import useWorkflowStore from '@/stores/WorkflowStore';
import { useShallow } from 'zustand/react/shallow';

import { cn } from '@/lib/utils';
import { checkWorkflowMatchedContacts } from '@/hooks/workflow';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

const WorkflowSettingsDialog = ({
  isOpen,
  onOpenChange,
  workflowFirstActivatedAt = null,
  mode = 'create',
  onSave,
}) => {
  const {
    trigger,
    channel,
    reEnrollment,
    enrollExisting,
    removeIfIneligible,
    isActive,
    setIsActive,
    setReEnrollment,
    setEnrollExisting,
    setRemoveIfIneligible,
  } = useWorkflowStore(
    useShallow((state) => ({
      trigger: state.tempTrigger,
      channel: state.channels,
      reEnrollment: state.reEnrollment,
      enrollExisting: state.enrollExisting,
      removeIfIneligible: state.removeIfIneligible,
      isActive: state.isActive,
      setIsActive: state.setIsActive,
      setReEnrollment: state.setReEnrollment,
      setEnrollExisting: state.setEnrollExisting,
      setRemoveIfIneligible: state.setRemoveIfIneligible,
    }))
  );
  const [matchedCount, setMatchedCount] = useState(null);
  const [isLoadingCount, setIsLoadingCount] = useState(false);

  const [tempReEnrollment, setTempReEnrollment] = useState(reEnrollment);
  const [tempEnrollExisting, setTempEnrollExisting] = useState(enrollExisting);
  const [tempRemoveIfIneligible, setTempRemoveIfIneligible] =
    useState(removeIfIneligible);

  // Helper function to check matched contacts
  const handleCheckMatchedContacts = async (
    setMatchedCount,
    setIsLoadingCount
  ) => {
    setIsLoadingCount(true);
    const triggerDetails = {
      channel: channel,
      triggers: trigger,
    };
    try {
      setMatchedCount(null); // Reset count when opening/dependencies change
      const result = await checkWorkflowMatchedContacts(triggerDetails);
      setMatchedCount(result.data.count);
      setIsLoadingCount(false);
    } catch (error) {
      setMatchedCount(null); // Reset count on error
      setIsLoadingCount(false); // Ensure loading is set to false on error
      console.error('Failed to check matched contacts.'); // Re-throw for toast.promise
    }
  };

  useEffect(() => {
    if (isOpen && trigger?.length > 0 && channel?.length > 0) {
      handleCheckMatchedContacts(setMatchedCount, setIsLoadingCount);
    }

    // Dependency array remains the same
  }, [isOpen, trigger, channel]);

  const handleReEnrollmentChange = (value) => {
    setTempReEnrollment(value === 'yes');
  };

  const handleEnrollExistingChange = (value) => {
    setTempEnrollExisting(value === 'yes');
  };

  const handleRemoveIfIneligibleChange = (value) => {
    setTempRemoveIfIneligible(value === 'yes');
  };

  const handleSettingModalClose = () => {
    onOpenChange(false);
    setTempReEnrollment(reEnrollment);
    setTempEnrollExisting(enrollExisting);
    setTempRemoveIfIneligible(removeIfIneligible);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleSettingModalClose}>
      <DialogContent className='sm:max-w-3xl'>
        <DialogHeader>
          <DialogTitle>Workflow Settings</DialogTitle>
          <DialogDescription>
            Configure how this workflow operates and enrolls contacts.
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-12 py-4'>
          {trigger?.length > 0 && trigger[0].type === 'schedule' && (
            <div className='flex flex-col gap-1 rounded-lg border bg-muted p-1'>
              <Label className='text-base'>Schedule</Label>
              <p className='text-sm text-muted-foreground'>
                If a <b>Time Since Last Message</b> condition is included, the
                workflow will automatically exit for a contact when a new
                message is received from them.
              </p>
            </div>
          )}
          <div className={cn('flex space-y-3', mode === 'edit' && 'hidden')}>
            <div className='flex items-center gap-1 rounded-lg border bg-muted p-1'>
              <button
                onClick={() => setIsActive(true)}
                className={`rounded-md px-3 py-1 text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? 'bg-green-600 text-white shadow-sm'
                    : 'text-muted-foreground hover:bg-muted/50'
                }`}
              >
                Active
              </button>
              <button
                onClick={() => setIsActive(false)}
                className={`rounded-md px-3 py-1 text-sm font-medium transition-colors duration-200 ${
                  !isActive
                    ? 'bg-background text-muted-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-muted/50'
                }`}
              >
                Inactive
              </button>
            </div>
          </div>
          <div className='space-y-3'>
            <div>
              <Label className='text-base'>Contact Re-enrollment</Label>
              <p className='mb-2 text-sm text-muted-foreground'>
                {trigger?.length > 0 && trigger[0].type === 'schedule'
                  ? 'Re-enrollment is automatically disabled for schedule type workflows.'
                  : 'Can a contact be enrolled in this workflow more than once?'}
              </p>
              <RadioGroup
                value={tempReEnrollment ? 'yes' : 'no'}
                onValueChange={handleReEnrollmentChange}
                disabled={trigger?.length > 0 && trigger[0].type === 'schedule'}
              >
                <div className='mb-2 flex items-start space-x-2'>
                  <RadioGroupItem value='yes' id='reenroll-yes' />
                  <div className='grid gap-1 leading-none'>
                    <Label className='cursor-pointer' htmlFor='reenroll-yes'>
                      Yes, contacts can be enrolled multiple times
                    </Label>
                  </div>
                </div>
                <div className='flex items-start space-x-2'>
                  <RadioGroupItem value='no' id='reenroll-no' />
                  <div className='grid gap-1 leading-none'>
                    <Label className='cursor-pointer' htmlFor='reenroll-no'>
                      No, contacts can only be enrolled once
                    </Label>
                  </div>
                </div>
              </RadioGroup>
            </div>
          </div>

          <div className='space-y-3'>
            <div>
              <Label className='text-base'>Enroll Existing Contacts</Label>
              <p className='mb-2 text-sm text-muted-foreground'>
                Should contacts already meeting the trigger conditions be
                enrolled?
              </p>
              <RadioGroup
                value={tempEnrollExisting ? 'yes' : 'no'}
                onValueChange={handleEnrollExistingChange}
              >
                <div className='mb-2 flex items-start space-x-2'>
                  <RadioGroupItem
                    value='yes'
                    id='enroll-existing-yes'
                    disabled={
                      isLoadingCount ||
                      matchedCount === null ||
                      matchedCount === 0
                    }
                  />
                  <div className='grid gap-1 leading-none'>
                    <Label
                      className='cursor-pointer'
                      htmlFor='enroll-existing-yes'
                    >
                      Yes, enroll existing contacts
                      {trigger?.length > 0 && channel?.length > 0 && (
                        <span className='ml-1 text-xs text-muted-foreground'>
                          {isLoadingCount
                            ? '(Checking...)'
                            : matchedCount !== null
                              ? `(${matchedCount} contacts)`
                              : ''}
                        </span>
                      )}
                    </Label>
                  </div>
                </div>
                <div className='flex items-start space-x-2'>
                  <RadioGroupItem value='no' id='enroll-existing-no' />
                  <div className='grid gap-1 leading-none'>
                    <Label
                      className='cursor-pointer'
                      htmlFor='enroll-existing-no'
                    >
                      No, only enroll new matching contacts
                    </Label>
                  </div>
                </div>
              </RadioGroup>
            </div>
          </div>

          <div className='space-y-3'>
            <div>
              <Label className='text-base'>
                When a contact no longer meets the enrollment conditions, remove
                them from this workflow?
              </Label>
              <RadioGroup
                value={tempRemoveIfIneligible ? 'yes' : 'no'}
                onValueChange={handleRemoveIfIneligibleChange}
                className='mt-2'
              >
                <div className='mb-2 flex items-start space-x-2'>
                  <RadioGroupItem value='yes' id='remove-yes' />
                  <div className='grid gap-1 leading-none'>
                    <Label className='cursor-pointer' htmlFor='remove-yes'>
                      Yes, remove them from this workflow
                    </Label>
                  </div>
                </div>
                <div className='flex items-start space-x-2'>
                  <RadioGroupItem value='no' id='remove-no' />
                  <div className='grid gap-1 leading-none'>
                    <Label className='cursor-pointer' htmlFor='remove-no'>
                      No, keep them in this workflow
                    </Label>
                  </div>
                </div>
              </RadioGroup>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={handleSettingModalClose}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              onSave({
                ...(mode === 'create' ? { is_active: isActive } : {}),
                re_enrollment: tempReEnrollment,
                enroll_existing: tempEnrollExisting,
                remove_if_ineligible: tempRemoveIfIneligible,
              })
            }
          >
            Save Settings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WorkflowSettingsDialog;
